import { useCallback, useEffect, useState } from 'react';

import Title from '@zenbusiness/common-components/Title';
import decode from 'jwt-decode';
import PropTypes from 'prop-types';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

import { MODAL_PARAM_KEY } from '#client/pages/Home/constants';
import { appendUrlParam, removeUrlParam } from '#client/pages/Home/utils';

import AccountHolder from './AccountHolder';
import ConnectWithPartners from './ConnectWithPartners';

import EditModal from './Modals/EditAccount';
import OptOutModal from './Modals/OptOut';
import useStyles from './useStyles';

const AccountSettings = ({ account, loading, refresh }) => {
  const classes = useStyles();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [currentModal, setCurrentModal] = useState(null);
  const { allPartnerOptOut } = account;

  const handleOpenModal = useCallback((modal) => {
    setCurrentModal(modal);
    appendUrlParam({
      navigate,
      location,
      paramName: modal,
      paramKey: MODAL_PARAM_KEY
    });
  }, [navigate, location]);

  const handleCloseModal = () => {
    setCurrentModal(null);
    removeUrlParam({ navigate, location, paramKey: MODAL_PARAM_KEY });
  };

  useEffect(() => {
    const modalName = searchParams.get(MODAL_PARAM_KEY);
    if (!modalName || currentModal) return;
    handleOpenModal(modalName);
  }, [currentModal, handleOpenModal, searchParams]);

  const idToken = localStorage.getItem('id_token');
  const idTokenDecoded = idToken ? decode(idToken) : null;
  const auth0Id = idTokenDecoded ? idTokenDecoded.sub : null;
  const socialLoginPrefixes = ['google-oauth2', 'apple'];
  const isSocialLogin = auth0Id ? socialLoginPrefixes.includes(auth0Id.split('|')[0]) : false;

  return (
    <section className={classes.root}>
      <Title
        className={classes.heading1}
        level={1}
        variant="lg-alt"
      >
        Account Settings
      </Title>
      <AccountHolder
        account={account}
        isSocialLogin={isSocialLogin}
        loading={loading}
        onClickEdit={() => handleOpenModal('editAccount')}
      />
      <ConnectWithPartners
        account={account}
        loading={loading}
        onClickOptOut={() => handleOpenModal('optOut')}
      />
      <EditModal
        account={account}
        isOpen={currentModal === 'editAccount'}
        isSocialLogin={isSocialLogin}
        onClose={() => handleCloseModal('editAccount')}
      />
      {!allPartnerOptOut
        && (
        <OptOutModal
          isOpen={currentModal === 'optOut'}
          onClose={() => handleCloseModal('optOut')}
          refresh={refresh}
        />
        )}
    </section>
  );
};

AccountSettings.propTypes = {
  account: PropTypes.shape({
    allPartnerOptOut: PropTypes.bool
  }).isRequired,
  loading: PropTypes.bool,
  refresh: PropTypes.func.isRequired
};

export default AccountSettings;
